import { useEffect } from "react";
import { useDispatch } from "react-redux";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { Resource } from "@mapmycustomers/shared/types/entity";
import { EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";

import { subscribe, UiSyncListener, unsubscribe } from "@app/store/uiSync/actions";
import applyEmailObjectsChanges from "@app/util/email/applyEmailObjectsChanges";

export interface UseEmailObjectsListenersArgs {
  entities: EntitiesSupportedByEmailFeature[];
  onEntitiesChange: (value: EntitiesSupportedByEmailFeature[]) => void;
  onResourcesChange: (value: Resource[]) => void;
  resources: ReadonlyMap<Resource["id"], Resource>;
}

const useEmailObjectsListeners = ({
  entities,
  onEntitiesChange,
  onResourcesChange,
  resources,
}: UseEmailObjectsListenersArgs) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const resourcesListenerTypes: EntityType[] = [EntityType.RESOURCE];
    const resourcesListener: UiSyncListener = {
      onDelete: (changedObjectsType, deletedObjectIds) => {
        onResourcesChange(
          applyEmailObjectsChanges({
            changedObjectsType,
            deletedObjectIds,
            objects: Array.from(resources.values()),
            objectsTypes: resourcesListenerTypes,
          })
        );
      },
      onUpdate: (changedObjectsType, updatedObjects) => {
        onResourcesChange(
          applyEmailObjectsChanges({
            changedObjectsType,
            objects: Array.from(resources.values()),
            objectsTypes: resourcesListenerTypes,
            updatedObjects,
          })
        );
      },
    };

    const entitiesListenerTypes: EntityType[] = [EntityType.COMPANY, EntityType.PERSON];
    const entitiesListener: UiSyncListener = {
      onDelete: (changedObjectsType, deletedObjectIds) => {
        onEntitiesChange(
          applyEmailObjectsChanges({
            changedObjectsType,
            deletedObjectIds,
            objects: entities,
            objectsTypes: entitiesListenerTypes,
          })
        );
      },
      onUpdate: (changedObjectsType, updatedObjects) => {
        onEntitiesChange(
          applyEmailObjectsChanges({
            changedObjectsType,
            objects: entities,
            objectsTypes: entitiesListenerTypes,
            updatedObjects,
          }).filter((entity) => !!entity.email)
        );
      },
    };

    dispatch(subscribe({ entityTypes: resourcesListenerTypes, listener: resourcesListener }));
    dispatch(
      subscribe({
        entityTypes: entitiesListenerTypes,
        listener: entitiesListener,
      })
    );

    return () => {
      dispatch(unsubscribe({ entityTypes: resourcesListenerTypes, listener: resourcesListener }));
      dispatch(unsubscribe({ entityTypes: entitiesListenerTypes, listener: entitiesListener }));
    };
  }, [dispatch, entities, onEntitiesChange, onResourcesChange, resources]);
};

export default useEmailObjectsListeners;
